/**
 * Hook for automatic Cognito token refresh
 * Requirements: 6.3 - Session management, 12.4 - Token expiration handling
 */
import { useEffect, useRef, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

// Refresh token 5 minutes before expiration
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export interface UseTokenRefreshOptions {
  isAuthenticated: boolean;
  onTokenExpired?: () => void;
}

export interface UseTokenRefreshReturn {
  forceRefresh: () => Promise<void>;
}

export const useTokenRefresh = ({
  isAuthenticated,
  onTokenExpired,
}: UseTokenRefreshOptions): UseTokenRefreshReturn => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTokenExpiredRef = useRef(onTokenExpired);

  // Keep callback ref updated
  useEffect(() => {
    onTokenExpiredRef.current = onTokenExpired;
  }, [onTokenExpired]);

  const clearRefreshTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const forceRefresh = useCallback(async (): Promise<void> => {
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch {
      onTokenExpiredRef.current?.();
      throw new Error('Token refresh failed');
    }
  }, []);

  const scheduleRefresh = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken;

      if (!accessToken?.payload?.exp) {
        onTokenExpiredRef.current?.();
        return;
      }

      const expirationTime = accessToken.payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;
      const refreshTime = timeUntilExpiry - REFRESH_BUFFER_MS;

      if (refreshTime <= 0) {
        // Token is about to expire or already expired, refresh immediately
        await forceRefresh();
        // Schedule next refresh
        scheduleRefresh();
      } else {
        // Schedule refresh before expiration
        clearRefreshTimer();
        timeoutRef.current = setTimeout(async () => {
          try {
            await forceRefresh();
            scheduleRefresh();
          } catch {
            // onTokenExpired already called in forceRefresh
          }
        }, refreshTime);
      }
    } catch {
      onTokenExpiredRef.current?.();
    }
  }, [forceRefresh, clearRefreshTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearRefreshTimer();
      return;
    }

    scheduleRefresh();

    return () => {
      clearRefreshTimer();
    };
  }, [isAuthenticated, scheduleRefresh, clearRefreshTimer]);

  return { forceRefresh };
};
